import { ReactElement, ReactNode, useEffect } from 'react'
import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material'
import { NextPage } from 'next'
import { type AppProps } from 'next/app'
import { Inter } from 'next/font/google'

import Maintenance from '@/components/pages/maintenance'
import DialogsProvider from '@/contexts/dialogs'
import { NotificationProvider } from '@/contexts/notification'
import { SWRProvider } from '@/contexts/swr'
import muiTheme from '@/styles/theme'

// Initialize i18n
import '@/contexts/i18n'

// Set to true to show a maintenance page instead of the app and block login
const MAINTENANCE_MODE = false

const inter = Inter({
  weight: '500',
  variable: '--base-font',
  subsets: ['latin'],
  display: 'swap'
})

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement, props: AppProps) => ReactNode
}

export type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page)

  useEffect(() => {
    if (document.body) document.body.classList.add(inter.variable)
  }, [])

  if (MAINTENANCE_MODE) {
    return (
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        <Maintenance />
      </MuiThemeProvider>
    )
  }

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <NotificationProvider>
        <DialogsProvider>
          <SWRProvider>{getLayout(<Component {...pageProps} />, pageProps)}</SWRProvider>
        </DialogsProvider>
      </NotificationProvider>
    </MuiThemeProvider>
  )
}
